// React hooks
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// Helpers i API servisi
import { transformRoute, useTranslate } from "helpers";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";
import {
  deleteFleetVehicleDriverAPI,
  getFleetVehicleDriversAPI,
} from "services/api/fleet/vehicles/drivers/api";
import { initialFleetVehicleDriverSearchForm } from "services/api/fleet/vehicles/drivers/consts";
import {
  FleetVehicleDriverRow,
  FleetVehicleDriverSearchFormValues,
} from "services/api/fleet/vehicles/drivers/types";

// UI komponente
import { Breadcrumb } from "ui/components/Breadcrumb";
import { Dialog } from "ui/components/Dialog";
import { NoResults } from "ui/components/NoResults";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { FLEET_DRIVER_SEARCH_PAGE_TITLE } from "ui/components/PageTitle/consts";
import { invalidateDrivers } from "ui/components/Dropdown/DriverDropdown/DriverDropdown";

// Moduli specifični za projekt
import { Routes as FleetVehicleRoutes } from "ui/modules/fleet/vehicles/navigation";
import VehicleDriverEditCard from "ui/modules/fleet/vehicles/pages/VehicleDetailPage/components/VehicleDetailCard/components/VehicleDriverEditCard/VehicleDriverEditCard";
import VehicleDriverSearchResults from "ui/modules/fleet/vehicles/pages/VehicleDetailPage/components/VehicleDetailCard/components/VehicleDriverSearchPage/components/VehicleDriverSearchResults/VehicleDriverSearchResults";

// Lokalna navigacija
import { Routes } from "ui/modules/fleet/drivers/navigation";

// Lokalne komponente
import DriverSearchFilters from "./components/DriverSearchFilters/DriverSearchFilters";
import { DriverSearchPageCmp, DriverSearchPageProps } from "./types";

/**
 * Vozači kroz sva vozila flote. Isti popis kao na kartici vozila, samo bez filtra po
 * vozilu — pa svaki redak nosi i karticu vozila.
 */
const DriverSearchPage: DriverSearchPageCmp = (_props: DriverSearchPageProps) => {
  const { t } = useTranslate();
  const navigate = useNavigate();

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<FleetVehicleDriverRow>
  >(initialDataWithPagination);
  const [filterValues, setFilterValues] =
    useState<FleetVehicleDriverSearchFormValues>(
      initialFleetVehicleDriverSearchForm
    );
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [driverId, setDriverId] = useState<string>("");
  // Izmjena vozača ide uz njegovo vozilo — zapis ga nosi, pa se čita iz retka.
  const [editVehicleId, setEditVehicleId] = useState<string>("");
  const [showEditCard, setShowEditCard] = useState<boolean>(false);
  const [deleteDriverId, setDeleteDriverId] = useState<string | null>(null);

  useEffect(() => {
    fetchSearchResult(initialDataWithPagination.pagination, filterValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSearchResult = async (
    pagination: Pagination,
    values: FleetVehicleDriverSearchFormValues
  ) => {
    setIsLoading(true);
    try {
      setSearchResults(
        await getFleetVehicleDriversAPI(pagination, undefined, values)
      );
    } catch {
      setSearchResults(initialDataWithPagination);
    }
    setIsLoading(false);
  };

  const handleSearchChange = useCallback(
    (values: FleetVehicleDriverSearchFormValues) => {
      setFilterValues(values);
      fetchSearchResult(searchResults.pagination, values);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchResults.pagination]
  );

  const handlePaginationChange = useCallback(
    (pagination: Pagination) => {
      fetchSearchResult(pagination, filterValues);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterValues]
  );

  // Vozač se uređuje istim čarobnjakom kao s kartice vozila.
  const handleRowClick = useCallback(
    (id: string) => {
      const row = searchResults.data.find((driver) => driver.id === id);

      setDriverId(id);
      setEditVehicleId(row?.vehicle?.id ?? "");
      setShowEditCard(true);
    },
    [searchResults.data]
  );

  const handleEditClose = useCallback(() => {
    setShowEditCard(false);
    setDriverId("");
    setEditVehicleId("");
  }, []);

  const handleEditSuccess = useCallback(() => {
    setShowEditCard(false);
    setDriverId("");
    setEditVehicleId("");
    invalidateDrivers();
    fetchSearchResult(searchResults.pagination, filterValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResults.pagination, filterValues]);

  const handleVehicleClick = useCallback(
    (event: React.MouseEvent, vehicleId: string) => {
      event.stopPropagation();
      navigate(
        transformRoute(FleetVehicleRoutes.FleetVehicleDetailPageRouter, {
          id: vehicleId,
        })
      );
    },
    [navigate]
  );

  const handleDeleteClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>, id: string) => {
      event.stopPropagation();
      event.preventDefault();

      setDeleteDriverId(id);
    },
    []
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteDriverId) return;

    try {
      await deleteFleetVehicleDriverAPI(deleteDriverId, t);
      // Obrisani vozač mora nestati i iz dropdowna na premještaju i statusu.
      invalidateDrivers();
      fetchSearchResult(searchResults.pagination, filterValues);
    } catch (err) {
      console.error("Greška prilikom brisanja vozača:", err);
    }

    setDeleteDriverId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteDriverId, t, searchResults.pagination, filterValues]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteDriverId(null);
  }, []);

  const handleSelectedChange = useCallback((selected: number[] | number) => {
    setSelectedRows(Array.isArray(selected) ? selected : [selected]);
  }, []);

  return (
    <>
      <div className="">
        <div className="bg-white rounded-t-md border border-b mb-2">
          <PageHeader>
            <PageTitle
              title={FLEET_DRIVER_SEARCH_PAGE_TITLE.text}
              icon={FLEET_DRIVER_SEARCH_PAGE_TITLE.icon}
              iconColor={FLEET_DRIVER_SEARCH_PAGE_TITLE.iconColor}
            />
            <Breadcrumb />
          </PageHeader>
        </div>
        <div className="px-0 py-6 mx-[-1px] xl:px-6">
          <DriverSearchFilters
            formValues={filterValues}
            onSearchChange={handleSearchChange}
          />
          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults message="Nema vozača" />
          ) : (
            <VehicleDriverSearchResults
              searchResults={searchResults}
              selectedRows={selectedRows}
              isLoading={isLoading}
              onRowClick={handleRowClick}
              onVehicleClick={handleVehicleClick}
              onDeleteClick={handleDeleteClick}
              onPaginationChange={handlePaginationChange}
              onSelectedChange={handleSelectedChange}
            />
          )}
        </div>
      </div>

      {showEditCard && (
        <VehicleDriverEditCard
          show={showEditCard}
          vehicleId={editVehicleId}
          driverId={driverId}
          onSuccess={handleEditSuccess}
          onClose={handleEditClose}
        />
      )}

      <Dialog
        open={!!deleteDriverId}
        title="Brisanje vozača"
        message="Jeste li sigurni da želite obrisati vozača?"
        confirmText="Obriši"
        cancelText="Odustani"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </>
  );
};

DriverSearchPage.route = Routes.DriverSearchPageRouter;

export default DriverSearchPage;
